import React, { useState } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ThemeProvider } from '@mui/material/styles';
import { AuthProvider } from './contexts/authWindows';
import { AppContent } from './AppContent';
import { LocalizationProvider } from '@mui/lab';
import { SnackbarProvider } from 'notistack';
import { useLocation } from 'react-router-dom';
import { fullscreenPathPrefix } from './utils/core/routes';
import { BrowserRouter } from 'react-router-dom';
import { AppContentFullScreen } from './AppContentFullScreen';
import Typography from '@mui/material/Typography';
import * as api from './api/app/appInfoApi';
import { AppInfo } from './types/app/appInfo.types';
import { TranslationProvider } from './contexts/translation';
import { cs, enGB, de } from 'date-fns/locale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from './contexts/translation';
import { darkTheme, lightTheme, ThemeType, useLocalTheme } from './hooks/useLocalTheme';

function App({ connectionState }: { connectionState: boolean }) {
  const { pathname } = useLocation();
  const { lang } = useTranslation();

  let locale;
  if (lang === 'en') locale = enGB;
  if (lang === 'de') locale = de;
  if (lang === 'cs') locale = cs;

  if (!connectionState) return <ConnectionStateError />;
  if (pathname.includes(fullscreenPathPrefix))
    return (
      <LocalizationProvider locale={locale} dateAdapter={AdapterDateFns}>
        <AppContentFullScreen />
      </LocalizationProvider>
    );

  return (
    <LocalizationProvider locale={locale} dateAdapter={AdapterDateFns}>
      <AppContent />
    </LocalizationProvider>
  );
}

function ConnectionStateError() {
  useQuery<AppInfo>('AppInfo', api.getAppInfo, {
    refetchInterval: 10000,
    onSuccess: () => {},
  });

  return (
    <Typography style={{ textAlign: 'center', marginTop: '15%', fontSize: '4em', color: 'red' }}>
      Spojení se serverem selhalo
    </Typography>
  );
}

export default function AppContainer() {
  const [connectionState, setConnectionState] = useState<boolean>(true);
  const { localTheme } = useLocalTheme();

  const queryClient = new QueryClient({});

  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <TranslationProvider>
          <BrowserRouter>
            <AuthProvider>
              <ThemeProvider theme={localTheme === ThemeType.DARK ? darkTheme : lightTheme}>
                <App connectionState={connectionState} />
                <ReactQueryDevtools />
              </ThemeProvider>
            </AuthProvider>
          </BrowserRouter>
        </TranslationProvider>
      </SnackbarProvider>
    </QueryClientProvider>
  );
}
